import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Tilt } from 'react-tilt';
import Particles from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { FiMail, FiMapPin, FiGithub, FiLinkedin, FiTwitter, FiCopy } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import toast, { Toaster } from 'react-hot-toast';
import api from '../utils/api.js';

const Contact = () => {
  const [availability, setAvailability] = useState({ status: 'Not Available', message: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const res = await api.get('/api/availability');
        setAvailability(res.data);
      } catch (error) {
        console.error('Error fetching availability:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAvailability();
  }, []);

  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const particlesOptions = {
    particles: {
      number: { value: 50, density: { enable: true, value_area: 800 } },
      color: { value: '#888888' },
      shape: { type: 'circle' },
      opacity: { value: 0.5, random: true },
      size: { value: 3, random: true },
      line_linked: { enable: true, distance: 150, color: '#888888', opacity: 0.4, width: 1 },
      move: { enable: true, speed: 2, direction: 'none', random: true, out_mode: 'out' },
    },
    interactivity: {
      detect_on: 'canvas',
      events: { onhover: { enable: true, mode: 'grab' }, onclick: { enable: true, mode: 'push' }, resize: true },
      modes: { grab: { distance: 200, line_linked: { opacity: 0.5 } }, push: { particles_nb: 4 } },
    },
    retina_detect: true,
  };

  const copyEmail = () => {
    navigator.clipboard.writeText('osmangranday@gmail.com');
    toast.success('Email copied to clipboard!', {
      position: 'top-right',
      duration: 2000,
      style: { background: '#10B981', color: '#fff' },
    });
  };

  return (
    <>
      <Helmet>
        <title>Contact - Portfolio</title>
        <meta name="description" content="Connect with Osman Ghani Granday for collaboration opportunities or to discuss innovative projects" />
      </Helmet>

      <div className="relative pt-20 section-padding bg-dark-900 min-h-screen overflow-hidden">
        <Particles
          id="tsparticles"
          init={particlesInit}
          options={particlesOptions}
          className="absolute inset-0 z-0"
        />
        <Toaster />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-center mb-6 text-gray-900 dark:text-gray-100">
              Connect With Me
            </h1>
            <p className="text-xl text-center mb-12 max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              I'm excited to collaborate on innovative projects or discuss opportunities in tech. Reach out via email or connect on social platforms!
            </p>

            <div className="flex flex-col lg:flex-row gap-12 items-center justify-center">
              {/* 3D Contact Card */}
              <Tilt className="Tilt" options={{ max: 15, scale: 1.02, speed: 400, glare: true, "max-glare": 0.2 }}>
                <div className="glass-effect p-8 max-w-md w-full shadow-2xl rounded-[2rem] border border-white/10 relative overflow-hidden group">
                  {/* Background Decoration */}
                  <div className="absolute -right-10 -bottom-10 opacity-[0.05] group-hover:opacity-[0.1] transition-all duration-700 pointer-events-none">
                    <FiGithub size={200} />
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                      <h2 className="text-2xl font-bold text-white tracking-tight">Osman Ghani Granday</h2>
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
                          availability.status === 'Open to Work'
                            ? 'bg-green-500/10 text-green-400 border-green-500/20'
                            : availability.status === 'Available for Freelance'
                            ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                            : 'bg-red-500/10 text-red-400 border-red-500/20'
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-current mr-2 animate-pulse" />
                        {loading ? 'Loading...' : availability.status}
                      </span>
                    </div>
                    
                    <div className="space-y-6">
                      <ContactItem
                        icon={<FiMail size={18} />}
                        title="Email"
                        content="osmangranday@gmail.com"
                        action={<FiCopy size={16} className="cursor-pointer" onClick={copyEmail} />}
                      />
                      <ContactItem
                        icon={<FiMapPin size={18} />}
                        title="Location"
                        content="Afghanistan"
                      />
                    </div>

                    {availability.message && (
                      <div className="mt-8 p-4 rounded-xl bg-white/5 border border-white/5">
                        <p className="text-gray-400 text-sm italic font-light leading-relaxed">
                          "{availability.message}"
                        </p>
                      </div>
                    )}

                    <div className="flex justify-center gap-4 mt-10">
                      <SocialLink href="https://github.com/MrGranday" icon={<FiGithub size={20} /> } />
                      <SocialLink href="https://www.linkedin.com/in/osman-ghani-granday-368b18259/" icon={<FiLinkedin size={20} />} />
                      <SocialLink href="https://x.com/OGranday" icon={<FiTwitter size={20} />} />
                    </div>
                  </div>
                </div>
              </Tilt>

              {/* Why Work With Me */}
              <div className="max-w-md w-full">
                <div className="glass-effect p-8 shadow-2xl rounded-[2rem] border border-white/5 relative overflow-hidden group">
                   <div className="absolute -right-6 -top-6 opacity-[0.03] group-hover:opacity-[0.06] transition-all duration-700 pointer-events-none">
                    <FiMapPin size={150} />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-8 text-white tracking-tight relative z-10">Why Work With Me?</h3>
                  <ul className="space-y-5 relative z-10">
                    {[
                      '5 years of professional experience',
                      'Full-stack development expertise',
                      'Strong focus on user experience',
                      'Agile development methodology',
                      'Excellent communication skills',
                    ].map((item, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-center text-gray-400 font-light group/item"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary_accent mr-4 group-hover/item:scale-150 transition-transform" />
                        <span className="group-hover/item:text-gray-200 transition-colors">{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

const ContactItem = ({ icon, title, content, action }) => (
  <div className="flex items-center justify-between group/item">
    <div className="flex items-center space-x-4">
      <div className="p-3.5 rounded-xl bg-white/5 text-secondary_accent border border-white/5 group-hover/item:border-secondary_accent/30 group-hover/item:bg-secondary_accent/10 transition-all duration-300">
        {icon}
      </div>
      <div>
        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">{title}</h3>
        <p className="text-gray-200 font-medium group-hover/item:text-white transition-colors">{content}</p>
      </div>
    </div>
    {action && (
      <button className="p-2.5 rounded-lg bg-white/5 text-gray-500 hover:text-secondary_accent border border-transparent hover:border-secondary_accent/20 hover:bg-secondary_accent/5 transition-all duration-300">
        {action}
      </button>
    )}
  </div>
);

const SocialLink = ({ href, icon }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="p-4 rounded-xl bg-white/5 border border-white/5 text-gray-400 hover:text-secondary_accent hover:border-secondary_accent/30 transition-all duration-300 backdrop-blur-sm"
    whileHover={{ y: -5, scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
  >
    {icon}
  </motion.a>
);

export default Contact;
